<div
  class="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 p-4 min-h-screen"
>
  <div class="max-w-5xl mx-auto">
    <!-- Barre de progression -->
    <app-progress-bar
      [currentStep]="currentStep"
      [totalSteps]="totalSteps"
    ></app-progress-bar>

    <!-- Titre -->
    <h2 class="text-xl font-bold mb-1 mt-3">📦 Mes Produits</h2>
    <p class="text-gray-600 dark:text-gray-400 text-xs mb-4">
      Sélectionnez vos produits depuis le catalogue et configurez vos prix.
    </p>

    <!-- Bouton Carte du menu -->
    @if (!loading && products.length > 0) {
      <button
        (click)="showMenuCard = true"
        class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 transition-colors mb-3"
      >
        📋 Carte du menu
      </button>
    }

    <!-- Menu Card Modal -->
    @if (showMenuCard) {
      <app-menu-card
        [products]="products"
        [merchantType]="merchantType === 'itinerant' ? 'itinerant' : 'fixed'"
        [businessName]="businessName"
        (closed)="showMenuCard = false"
      ></app-menu-card>
    }

    <!-- Loading State -->
    @if (loading) {
      <div class="flex items-center justify-center py-12">
        <div class="text-center">
          <div
            class="inline-block animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent"
          ></div>
          <p class="text-gray-600 dark:text-gray-400 text-sm mt-3">
            Chargement des produits...
          </p>
        </div>
      </div>
    }

    <!-- MAIN CONTENT - Products Editor -->
    @if (!loading) {
      <div class="mb-4">
        <app-products-editor
          [products]="products"
          [isOnboarding]="true"
          [merchantType]="merchantType"
          [showStats]="true"
          [validProducts]="validProducts"
          [totalProducts]="totalProducts"
          [totalStockPurchaseValue]="totalStockPurchaseValue"
          [totalStockSellValue]="totalStockSellValue"
          [productsAtRisk]="productsAtRisk"
          [autoSaving]="autoSaving"
          (productsChange)="onProductsChange($event)"
          (productSelected)="onProductSelected($event)"
          (templateApplied)="onTemplateApplied($event)"
          [showCatalogSearch]="showSearchCatalog"
          (closeCatalog)="showSearchCatalog = false"
        ></app-products-editor>
      </div>
    }

    <!-- Boutons de navigation -->
    <div class="flex justify-between mt-4">
      <!-- Bouton Retour -->
      <button
        (click)="goBack()"
        class="flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600 hover:text-gray-900 dark:hover:text-white dark:text-white"
      >
        ← Précédent
      </button>

      <!-- Bouton Continuer -->
      <button
        (click)="continue()"
        [disabled]="!canContinue || saving"
        class="flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-lg transition-all"
        [ngClass]="{
          'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md dark:shadow-slate-900/50 hover:shadow-lg dark:shadow-slate-900/50':
            canContinue && !saving,
          'bg-gray-300 text-gray-500 dark:text-gray-400 cursor-not-allowed':
            !canContinue || saving,
        }"
      >
        @if (!saving) {
          <span>Continuer →</span>
        }
        @if (saving) {
          <span>Sauvegarde...</span>
        }
      </button>
    </div>
  </div>

  <!-- DESKTOP: Slide-in Panel from Right (visible on lg+) -->
  @if (selectedProduct) {
    <div class="hidden lg:block fixed inset-0 z-50">
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/10 backdrop-blur-[1px] transition-opacity"
        (click)="selectedProduct = null"
      ></div>
      <!-- Slide-in Panel -->
      <aside
        class="absolute right-0 top-0 bottom-0 w-[420px] bg-white dark:bg-slate-800 shadow-2xl overflow-y-auto animate-slide-in-right"
      >
        <div
          class="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700 p-4 flex items-center justify-between z-10"
        >
          <h2 class="font-bold text-gray-900 dark:text-white">
            Détails du produit
          </h2>
          <button
            (click)="selectedProduct = null"
            class="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 rounded-lg transition-colors"
          >
            <svg
              class="w-5 h-5 text-gray-500 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div class="p-2 space-y-1.5">
          <!-- Product Header -->
          <div
            class="flex items-center gap-2 pb-1.5 border-b border-gray-100 dark:border-slate-700"
          >
            <div class="text-3xl">{{ selectedProduct.productIcon }}</div>
            <div class="flex-1">
              <div class="flex items-center gap-1">
                <h3 class="font-bold text-sm text-gray-900 dark:text-white">
                  {{ selectedProduct.name }}
                </h3>
                @if (selectedProduct.isCustom) {
                  <span
                    class="text-[9px] px-1 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full font-medium"
                  >
                    ✏️
                  </span>
                }
              </div>
              <p class="text-[10px] text-gray-500 dark:text-gray-400">
                {{ selectedProduct.category }}
              </p>
            </div>
          </div>
          <!-- Editable Fields -->
          <div class="space-y-1.5">
            <!-- Prix d'achat -->
            <div>
              <div class="flex items-center justify-between">
                <label
                  class="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase"
                  >Prix d'achat</label
                >
                <div class="relative w-20">
                  <input
                    type="number"
                    [(ngModel)]="selectedProduct.purchasePrice"
                    (ngModelChange)="onProductFieldChange()"
                    class="w-full px-1.5 py-0.5 pr-5 border border-gray-200 dark:border-slate-700 rounded text-right font-bold text-gray-900 dark:text-white text-sm"
                    step="0.01"
                    min="0"
                  />
                  <span
                    class="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                    >€</span
                  >
                </div>
              </div>
              <input
                type="range"
                [min]="0"
                [max]="20"
                [step]="0.1"
                [(ngModel)]="selectedProduct.purchasePrice"
                (ngModelChange)="onProductFieldChange()"
                class="w-full h-1.5 bg-gray-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
            <!-- Prix de vente -->
            <div>
              <div class="flex items-center justify-between">
                <label
                  class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
                  >Prix de vente</label
                >
                <div class="relative w-20">
                  <input
                    type="number"
                    [(ngModel)]="selectedProduct.sellPrice"
                    (ngModelChange)="onProductFieldChange()"
                    class="w-full px-2 py-0.5 pr-5 border border-gray-200 dark:border-slate-700 rounded-lg text-right font-bold text-gray-900 dark:text-white text-sm"
                    step="0.01"
                    min="0"
                  />
                  <span
                    class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                    >€</span
                  >
                </div>
              </div>
              <input
                type="range"
                [min]="0"
                [max]="40"
                [step]="0.1"
                [(ngModel)]="selectedProduct.sellPrice"
                (ngModelChange)="onProductFieldChange()"
                class="w-full h-1.5 bg-gray-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
            </div>
            <!-- Calcul Marge -->
            <div
              class="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-700 rounded p-1.5 flex items-center gap-2"
            >
              <div class="flex-1">
                <span class="text-[9px] text-gray-500 dark:text-gray-400"
                  >Marge</span
                >
                <div
                  class="text-sm font-bold"
                  [class.text-green-600]="getMargin(selectedProduct) >= 0"
                  [class.text-red-600]="getMargin(selectedProduct) < 0"
                >
                  {{ getMargin(selectedProduct) | number: "1.2-2" }}€
                </div>
              </div>
              <div class="flex-1 text-right">
                <span class="text-[9px] text-gray-500 dark:text-gray-400"
                  >% Marge</span
                >
                <div
                  class="text-sm font-bold"
                  [class.text-green-600]="
                    getMarginPercent(selectedProduct) >= 30
                  "
                  [class.text-orange-500]="
                    getMarginPercent(selectedProduct) >= 15 &&
                    getMarginPercent(selectedProduct) < 30
                  "
                  [class.text-red-600]="getMarginPercent(selectedProduct) < 15"
                >
                  {{ getMarginPercent(selectedProduct) | number: "1.0-0" }}%
                </div>
              </div>
            </div>
          </div>
          <!-- Périssabilité -->
          <div class="mt-2">
            <label
              class="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
              >Périssabilité</label
            >
            <div class="flex gap-1 mt-1">
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'high'; onProductFieldChange()
                "
                class="flex-1 px-2 py-1 rounded-lg text-[10px] font-medium border transition-all"
                [class.border-red-400]="
                  selectedProduct.perishability === 'high' ||
                  !selectedProduct.perishability
                "
                [class.bg-red-50]="
                  selectedProduct.perishability === 'high' ||
                  !selectedProduct.perishability
                "
                [class.text-red-700]="
                  selectedProduct.perishability === 'high' ||
                  !selectedProduct.perishability
                "
                [class.border-gray-200]="
                  selectedProduct.perishability &&
                  selectedProduct.perishability !== 'high'
                "
                [class.text-gray-500]="
                  selectedProduct.perishability &&
                  selectedProduct.perishability !== 'high'
                "
              >
                🥖 Jour même
              </button>
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'medium';
                  onProductFieldChange()
                "
                class="flex-1 px-2 py-1 rounded-lg text-[10px] font-medium border transition-all"
                [class.border-amber-400]="
                  selectedProduct.perishability === 'medium'
                "
                [class.bg-amber-50]="selectedProduct.perishability === 'medium'"
                [class.text-amber-700]="
                  selectedProduct.perishability === 'medium'
                "
                [class.border-gray-200]="
                  selectedProduct.perishability !== 'medium'
                "
                [class.text-gray-500]="
                  selectedProduct.perishability !== 'medium'
                "
              >
                ⚠️ 1-2j
              </button>
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'low'; onProductFieldChange()
                "
                class="flex-1 px-2 py-1 rounded-lg text-[10px] font-medium border transition-all"
                [class.border-sky-400]="selectedProduct.perishability === 'low'"
                [class.bg-sky-50]="selectedProduct.perishability === 'low'"
                [class.text-sky-700]="selectedProduct.perishability === 'low'"
                [class.border-gray-200]="
                  selectedProduct.perishability !== 'low'
                "
                [class.text-gray-500]="selectedProduct.perishability !== 'low'"
              >
                🧊 Longue
              </button>
            </div>
          </div>
          <!-- Stock avec Jauge intégrée -->
          <div
            class="bg-gray-50 dark:bg-slate-900 rounded-lg p-2 border border-gray-200 dark:border-slate-700"
          >
            <!-- Header: Stock avec input et unité -->
            <div class="flex items-center justify-between mb-1">
              <label
                class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
                >Stock</label
              >
              <div class="flex items-center gap-1">
                <input
                  type="number"
                  [(ngModel)]="selectedProduct.quantity"
                  (ngModelChange)="onProductFieldChange()"
                  class="w-14 px-1.5 py-0.5 border border-gray-200 dark:border-slate-700 rounded-lg text-right font-bold text-gray-900 dark:text-white text-sm"
                  min="0"
                />
                <select
                  [(ngModel)]="selectedProduct.unit"
                  (ngModelChange)="onProductFieldChange()"
                  class="px-1.5 py-0.5 border border-gray-200 dark:border-slate-700 rounded-lg text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 cursor-pointer"
                >
                  @for (unit of units; track unit) {
                    <option [value]="unit">
                      {{ unit }}
                    </option>
                  }
                </select>
              </div>
            </div>
            <!-- Jauge interactive -->
            <div class="relative">
              <!-- Barre de jauge tricolore avec slider custom -->
              <div
                class="relative h-4 rounded-full overflow-visible cursor-pointer select-none"
                (mousedown)="onGaugeMouseDown($event)"
                (mousemove)="onGaugeMouseMove($event)"
                (mouseup)="onGaugeMouseUp()"
                (mouseleave)="onGaugeMouseUp()"
              >
                <!-- Background avec zones colorées -->
                <div
                  class="absolute inset-0 rounded-full overflow-hidden flex pointer-events-none"
                >
                  <div
                    class="w-1/4 h-full bg-gradient-to-r from-red-400 to-red-300"
                  ></div>
                  <div
                    class="w-1/2 h-full bg-gradient-to-r from-green-400 to-green-300"
                  ></div>
                  <div
                    class="w-1/4 h-full bg-gradient-to-r from-amber-300 to-amber-400"
                  ></div>
                </div>
                <!-- Curseur visuel -->
                <div
                  class="absolute top-1/2 w-4 h-4 bg-white dark:bg-slate-800 border-2 border-gray-800 rounded-full shadow-md dark:shadow-slate-900/50 pointer-events-none z-20"
                  [style.left.%]="getStockGaugePosition(selectedProduct)"
                  style="transform: translate(-50%, -50%)"
                ></div>
                <!-- Marqueurs de seuils -->
                <div
                  class="absolute top-full mt-1 text-[10px] font-medium text-red-600 dark:text-red-400 -translate-x-1/2"
                  style="left: 25%"
                >
                  {{ getProductThresholds(selectedProduct).minStock }}
                </div>
                <div
                  class="absolute top-full mt-1 text-[10px] font-medium text-amber-600 -translate-x-1/2"
                  style="left: 75%"
                >
                  {{ getProductThresholds(selectedProduct).maxStock }}
                </div>
              </div>
              <!-- Légende avec valeurs -->
              <div
                class="flex justify-between text-[10px] text-gray-500 dark:text-gray-400 mt-5"
              >
                <span class="text-red-600 dark:text-red-400 font-medium"
                  >🚨 Rupture</span
                >
                <span class="text-green-600 dark:text-green-400 font-medium"
                  >✅ Optimal</span
                >
                <span class="text-amber-600 font-medium">📦 Surstock</span>
              </div>
            </div>
            <!-- Message d'alerte si risque -->
            @if (selectedProduct.riskType && !editingThresholds) {
              <div
                class="mt-3 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                [class.bg-red-100]="selectedProduct.riskType === 'rupture'"
                [class.text-red-700]="selectedProduct.riskType === 'rupture'"
                [class.bg-amber-100]="selectedProduct.riskType === 'surstock'"
                [class.text-amber-700]="selectedProduct.riskType === 'surstock'"
              >
                {{ getRiskMessage(selectedProduct) }}
              </div>
            }
            <!-- Bouton modifier les seuils (discret mais accessible) -->
            <div
              class="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700"
            >
              @if (!editingThresholds) {
                <button
                  (click)="openThresholdsEditor()"
                  class="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs text-gray-600 dark:text-gray-400 hover:text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:bg-purple-900/20 rounded-lg transition-colors"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                    />
                  </svg>
                  Ajuster les seuils d'alerte
                </button>
              }
              <!-- Formulaire d'édition des seuils -->
              @if (editingThresholds) {
                <div
                  class="p-3 bg-white dark:bg-slate-800 rounded-lg border border-purple-200 dark:border-purple-800 animate-fade-in"
                >
                  <div
                    class="text-xs font-medium text-purple-700 dark:text-purple-300 mb-3"
                  >
                    ✏️ Définir les seuils d'alerte
                  </div>
                  <div class="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label
                        class="text-[10px] text-gray-500 dark:text-gray-400 block mb-1"
                      >
                        🚨 Alerte rupture si &lt;
                      </label>
                      <input
                        type="number"
                        [(ngModel)]="tempThresholds.minStock"
                        class="w-full px-3 py-2 border-2 border-gray-200 dark:border-slate-700 rounded-lg text-sm text-center font-medium focus:border-red-400 dark:border-red-700"
                        min="0"
                      />
                    </div>
                    <div>
                      <label
                        class="text-[10px] text-gray-500 dark:text-gray-400 block mb-1"
                      >
                        📦 Alerte surstock si &gt;
                      </label>
                      <input
                        type="number"
                        [(ngModel)]="tempThresholds.maxStock"
                        class="w-full px-3 py-2 border-2 border-gray-200 dark:border-slate-700 rounded-lg text-sm text-center font-medium focus:border-amber-400"
                        min="1"
                      />
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <button
                      (click)="cancelThresholdsEdit()"
                      class="flex-1 px-3 py-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-600 dark:bg-slate-700 rounded-lg transition-colors font-medium"
                    >
                      Annuler
                    </button>
                    @if (hasCustomThresholds(selectedProduct)) {
                      <button
                        (click)="resetProductThresholds()"
                        class="px-3 py-2 text-xs text-orange-600 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:bg-orange-900/30 rounded-lg transition-colors"
                        title="Réinitialiser aux valeurs par défaut"
                      >
                        🔄 Reset
                      </button>
                    }
                    <button
                      (click)="saveCustomThresholds()"
                      class="flex-1 px-3 py-2 text-xs text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors font-medium"
                    >
                      ✓ Appliquer
                    </button>
                  </div>
                </div>
              }
            </div>
          </div>
          <!-- Popularité -->
          <div>
            <label
              class="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1"
              >Popularité</label
            >
            <select
              [(ngModel)]="selectedProduct.popularity"
              (ngModelChange)="onProductFieldChange()"
              class="w-full px-3 py-2.5 border border-gray-200 dark:border-slate-700 rounded-lg font-medium"
            >
              <option [ngValue]="2">⭐ Faible</option>
              <option [ngValue]="5">⭐⭐ Moyenne</option>
              <option [ngValue]="7">⭐⭐⭐ Forte</option>
              <option [ngValue]="9">🔥 Très forte</option>
            </select>
          </div>
          <!-- Périssabilité -->
          <div>
            <label
              class="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1"
              >Périssabilité</label
            >
            <div class="flex gap-1">
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'high'; onProductFieldChange()
                "
                class="flex-1 py-2 px-1 rounded-lg border-2 text-xs font-medium transition-all"
                [class.border-red-400]="
                  (selectedProduct.perishability ?? 'high') === 'high'
                "
                [class.bg-red-50]="
                  (selectedProduct.perishability ?? 'high') === 'high'
                "
                [class.text-red-700]="
                  (selectedProduct.perishability ?? 'high') === 'high'
                "
                [class.border-gray-200]="
                  (selectedProduct.perishability ?? 'high') !== 'high'
                "
                [class.text-gray-500]="
                  (selectedProduct.perishability ?? 'high') !== 'high'
                "
              >
                🥖 Jour même
              </button>
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'medium';
                  onProductFieldChange()
                "
                class="flex-1 py-2 px-1 rounded-lg border-2 text-xs font-medium transition-all"
                [class.border-amber-400]="
                  selectedProduct.perishability === 'medium'
                "
                [class.bg-amber-50]="selectedProduct.perishability === 'medium'"
                [class.text-amber-700]="
                  selectedProduct.perishability === 'medium'
                "
                [class.border-gray-200]="
                  selectedProduct.perishability !== 'medium'
                "
                [class.text-gray-500]="
                  selectedProduct.perishability !== 'medium'
                "
              >
                ⚠️ 1-2j
              </button>
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'low'; onProductFieldChange()
                "
                class="flex-1 py-2 px-1 rounded-lg border-2 text-xs font-medium transition-all"
                [class.border-sky-400]="selectedProduct.perishability === 'low'"
                [class.bg-sky-50]="selectedProduct.perishability === 'low'"
                [class.text-sky-700]="selectedProduct.perishability === 'low'"
                [class.border-gray-200]="
                  selectedProduct.perishability !== 'low'
                "
                [class.text-gray-500]="selectedProduct.perishability !== 'low'"
              >
                🧊 Stock
              </button>
            </div>
          </div>
          <!-- Conservation -->
          <div>
            <label
              class="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1"
              >Conservation</label
            >
            <select
              [(ngModel)]="selectedProduct.shelfLife"
              (ngModelChange)="onProductFieldChange()"
              class="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm font-medium"
            >
              <option value="jour même">📅 Jour même</option>
              <option value="2 jours">📅 2 jours</option>
              <option value="3 jours">📅 3 jours</option>
              <option value="1 semaine">📅 1 semaine</option>
              <option value="1 mois">📅 1 mois</option>
              <option value="6 mois">📅 6 mois</option>
              <option value="1 an">📅 1 an</option>
            </select>
          </div>
          <!-- Actions -->
          <div
            class="pt-3 border-t border-gray-100 dark:border-slate-700 flex gap-2"
          >
            <button
              (click)="removeProduct(selectedProduct); selectedProduct = null"
              class="flex-1 px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Retirer
            </button>
            <button
              (click)="confirmProduct()"
              class="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              [disabled]="saving"
            >
              @if (!saving) {
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              }
              @if (saving) {
                <svg
                  class="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              }
              {{ saving ? "Sauvegarde..." : "Confirmer" }}
            </button>
          </div>
        </div>
      </aside>
    </div>
  }

  <!-- MOBILE/TABLET: Bottom Sheet (visible on < lg) -->
  @if (selectedProduct) {
    <div class="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/10 backdrop-blur-[1px]"
        (click)="selectedProduct = null"
      ></div>
      <!-- Bottom Sheet -->
      <div
        class="relative bg-white dark:bg-slate-800 rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto animate-slide-up"
      >
        <!-- Handle -->
        <div
          class="sticky top-0 bg-white dark:bg-slate-800 pt-3 pb-2 border-b border-gray-100 dark:border-slate-700 z-10"
        >
          <div class="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-3"></div>
          <div class="px-4 flex items-center justify-between">
            <h2 class="font-bold text-gray-900 dark:text-white">
              Détails du produit
            </h2>
            <button
              (click)="selectedProduct = null"
              class="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800 rounded-lg"
            >
              <svg
                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        <div class="p-4 space-y-4 pb-8">
          <!-- Product Header -->
          <div
            class="flex items-center gap-4 pb-4 border-b border-gray-100 dark:border-slate-700"
          >
            <div class="text-5xl">{{ selectedProduct.productIcon }}</div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="font-bold text-lg text-gray-900 dark:text-white">
                  {{ selectedProduct.name }}
                </h3>
                @if (selectedProduct.isCustom) {
                  <span
                    class="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full font-medium"
                  >
                    ✏️ Perso
                  </span>
                }
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ selectedProduct.category }}
              </p>
            </div>
          </div>
          <!-- Prix d'achat -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
                >Prix d'achat</label
              >
              <div class="relative w-24">
                <input
                  type="number"
                  [(ngModel)]="selectedProduct.purchasePrice"
                  (ngModelChange)="onProductFieldChange()"
                  class="w-full px-2 py-1 pr-6 border border-gray-200 dark:border-slate-700 rounded-lg text-right font-bold text-gray-900 dark:text-white"
                  step="0.01"
                  min="0"
                />
                <span
                  class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                  >€</span
                >
              </div>
            </div>
            <input
              type="range"
              [min]="0"
              [max]="20"
              [step]="0.1"
              [(ngModel)]="selectedProduct.purchasePrice"
              (ngModelChange)="onProductFieldChange()"
              class="w-full h-1.5 bg-gray-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
          <!-- Prix de vente -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label
                class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
                >Prix de vente</label
              >
              <div class="relative w-24">
                <input
                  type="number"
                  [(ngModel)]="selectedProduct.sellPrice"
                  (ngModelChange)="onProductFieldChange()"
                  class="w-full px-2 py-1 pr-6 border border-gray-200 dark:border-slate-700 rounded-lg text-right font-bold text-gray-900 dark:text-white"
                  step="0.01"
                  min="0"
                />
                <span
                  class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                  >€</span
                >
              </div>
            </div>
            <input
              type="range"
              [min]="0"
              [max]="40"
              [step]="0.1"
              [(ngModel)]="selectedProduct.sellPrice"
              (ngModelChange)="onProductFieldChange()"
              class="w-full h-1.5 bg-gray-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-green-600"
            />
          </div>
          <!-- Marge -->
          <div
            class="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-700 rounded-lg p-3 flex items-center gap-4"
          >
            <div class="flex-1">
              <span class="text-xs text-gray-500 dark:text-gray-400"
                >Marge unitaire</span
              >
              <div
                class="text-lg font-bold"
                [class.text-green-600]="getMargin(selectedProduct) >= 0"
                [class.text-red-600]="getMargin(selectedProduct) < 0"
              >
                {{ getMargin(selectedProduct) | number: "1.2-2" }}€
              </div>
            </div>
            <div class="flex-1 text-right">
              <span class="text-xs text-gray-500 dark:text-gray-400"
                >% Marge</span
              >
              <div
                class="text-lg font-bold"
                [class.text-green-600]="getMarginPercent(selectedProduct) >= 30"
                [class.text-orange-500]="
                  getMarginPercent(selectedProduct) >= 15 &&
                  getMarginPercent(selectedProduct) < 30
                "
                [class.text-red-600]="getMarginPercent(selectedProduct) < 15"
              >
                {{ getMarginPercent(selectedProduct) | number: "1.0-0" }}%
              </div>
            </div>
          </div>
          <!-- Stock avec Jauge intégrée (Mobile) -->
          <div
            class="bg-gray-50 dark:bg-slate-900 rounded-xl p-3 border border-gray-200 dark:border-slate-700"
          >
            <!-- Header: Stock avec input et unité -->
            <div class="flex items-center justify-between mb-2">
              <label
                class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
              >
                Stock
              </label>
              <div class="flex items-center gap-1">
                <input
                  type="number"
                  [(ngModel)]="selectedProduct.quantity"
                  (ngModelChange)="onProductFieldChange()"
                  class="w-14 px-2 py-1 border border-gray-200 dark:border-slate-700 rounded-lg text-right font-bold text-gray-900 dark:text-white text-sm"
                  min="0"
                />
                <select
                  [(ngModel)]="selectedProduct.unit"
                  (ngModelChange)="onProductFieldChange()"
                  class="px-1 py-1 border border-gray-200 dark:border-slate-700 rounded-lg text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 cursor-pointer"
                >
                  @for (unit of units; track unit) {
                    <option [value]="unit">
                      {{ unit }}
                    </option>
                  }
                </select>
              </div>
            </div>
            <!-- Jauge interactive compacte -->
            <div class="relative">
              <div
                class="relative h-3 rounded-full overflow-visible cursor-pointer select-none"
                (mousedown)="onGaugeMouseDown($event)"
                (mousemove)="onGaugeMouseMove($event)"
                (mouseup)="onGaugeMouseUp()"
                (mouseleave)="onGaugeMouseUp()"
                (touchstart)="onGaugeTouchStart($event)"
                (touchmove)="onGaugeTouchMove($event)"
                (touchend)="onGaugeMouseUp()"
              >
                <!-- Background avec zones colorées -->
                <div
                  class="absolute inset-0 rounded-full overflow-hidden flex pointer-events-none"
                >
                  <div
                    class="w-1/4 h-full bg-gradient-to-r from-red-400 to-red-300"
                  ></div>
                  <div
                    class="w-1/2 h-full bg-gradient-to-r from-green-400 to-green-300"
                  ></div>
                  <div
                    class="w-1/4 h-full bg-gradient-to-r from-amber-300 to-amber-400"
                  ></div>
                </div>
                <!-- Curseur visuel -->
                <div
                  class="absolute top-1/2 w-3 h-3 bg-white dark:bg-slate-800 border-2 border-gray-800 rounded-full shadow-md dark:shadow-slate-900/50 pointer-events-none z-20"
                  [style.left.%]="getStockGaugePosition(selectedProduct)"
                  style="transform: translate(-50%, -50%)"
                ></div>
                <!-- Marqueurs de seuils -->
                <div
                  class="absolute top-full mt-0.5 text-[9px] font-medium text-red-600 dark:text-red-400 -translate-x-1/2"
                  style="left: 25%"
                >
                  {{ getProductThresholds(selectedProduct).minStock }}
                </div>
                <div
                  class="absolute top-full mt-0.5 text-[9px] font-medium text-amber-600 -translate-x-1/2"
                  style="left: 75%"
                >
                  {{ getProductThresholds(selectedProduct).maxStock }}
                </div>
              </div>
            </div>
            <!-- Message d'alerte si risque -->
            @if (selectedProduct.riskType && !editingThresholds) {
              <div
                class="mt-4 px-2 py-1.5 rounded-lg text-xs font-medium"
                [class.bg-red-100]="selectedProduct.riskType === 'rupture'"
                [class.text-red-700]="selectedProduct.riskType === 'rupture'"
                [class.bg-amber-100]="selectedProduct.riskType === 'surstock'"
                [class.text-amber-700]="selectedProduct.riskType === 'surstock'"
              >
                {{ getRiskMessage(selectedProduct) }}
              </div>
            }
            <!-- Bouton modifier les seuils -->
            <div
              class="mt-3 pt-2 border-t border-gray-200 dark:border-slate-700"
            >
              @if (!editingThresholds) {
                <button
                  (click)="openThresholdsEditor()"
                  class="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] text-gray-600 dark:text-gray-400 hover:text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:bg-purple-900/20 rounded-lg transition-colors"
                >
                  <svg
                    class="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                    />
                  </svg>
                  Ajuster seuils
                </button>
              }
              <!-- Formulaire d'édition des seuils -->
              @if (editingThresholds) {
                <div
                  class="p-2 bg-white dark:bg-slate-800 rounded-lg border border-purple-200 dark:border-purple-800"
                >
                  <div class="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label
                        class="text-[9px] text-gray-500 dark:text-gray-400 block mb-0.5"
                        >🚨 Rupture si &lt;</label
                      >
                      <input
                        type="number"
                        [(ngModel)]="tempThresholds.minStock"
                        class="w-full px-2 py-1.5 border-2 border-gray-200 dark:border-slate-700 rounded text-sm text-center font-medium"
                        min="0"
                      />
                    </div>
                    <div>
                      <label
                        class="text-[9px] text-gray-500 dark:text-gray-400 block mb-0.5"
                        >📦 Surstock si &gt;</label
                      >
                      <input
                        type="number"
                        [(ngModel)]="tempThresholds.maxStock"
                        class="w-full px-2 py-1.5 border-2 border-gray-200 dark:border-slate-700 rounded text-sm text-center font-medium"
                        min="1"
                      />
                    </div>
                  </div>
                  <div class="flex gap-1.5">
                    <button
                      (click)="cancelThresholdsEdit()"
                      class="flex-1 px-2 py-1.5 text-[10px] text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-slate-800 rounded-lg font-medium"
                    >
                      Annuler
                    </button>
                    @if (hasCustomThresholds(selectedProduct)) {
                      <button
                        (click)="resetProductThresholds()"
                        class="px-2 py-1.5 text-[10px] text-orange-600 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
                      >
                        🔄
                      </button>
                    }
                    <button
                      (click)="saveCustomThresholds()"
                      class="flex-1 px-2 py-1.5 text-[10px] text-white bg-purple-600 rounded-lg font-medium"
                    >
                      ✓ OK
                    </button>
                  </div>
                </div>
              }
            </div>
          </div>
          <!-- Popularité -->
          <div>
            <label
              class="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1"
              >Popularité</label
            >
            <select
              [(ngModel)]="selectedProduct.popularity"
              (ngModelChange)="onProductFieldChange()"
              class="w-full px-3 py-2.5 border border-gray-200 dark:border-slate-700 rounded-lg font-medium"
            >
              <option [ngValue]="2">⭐ Faible</option>
              <option [ngValue]="5">⭐⭐ Moyenne</option>
              <option [ngValue]="7">⭐⭐⭐ Forte</option>
              <option [ngValue]="9">🔥 Très forte</option>
            </select>
          </div>
          <!-- Périssabilité -->
          <div>
            <label
              class="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1"
              >Périssabilité</label
            >
            <div class="flex gap-1">
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'high'; onProductFieldChange()
                "
                class="flex-1 py-2 px-1 rounded-lg border-2 text-xs font-medium transition-all"
                [class.border-red-400]="
                  (selectedProduct.perishability ?? 'high') === 'high'
                "
                [class.bg-red-50]="
                  (selectedProduct.perishability ?? 'high') === 'high'
                "
                [class.text-red-700]="
                  (selectedProduct.perishability ?? 'high') === 'high'
                "
                [class.border-gray-200]="
                  (selectedProduct.perishability ?? 'high') !== 'high'
                "
                [class.text-gray-500]="
                  (selectedProduct.perishability ?? 'high') !== 'high'
                "
              >
                🥖 Jour même
              </button>
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'medium';
                  onProductFieldChange()
                "
                class="flex-1 py-2 px-1 rounded-lg border-2 text-xs font-medium transition-all"
                [class.border-amber-400]="
                  selectedProduct.perishability === 'medium'
                "
                [class.bg-amber-50]="selectedProduct.perishability === 'medium'"
                [class.text-amber-700]="
                  selectedProduct.perishability === 'medium'
                "
                [class.border-gray-200]="
                  selectedProduct.perishability !== 'medium'
                "
                [class.text-gray-500]="
                  selectedProduct.perishability !== 'medium'
                "
              >
                ⚠️ 1-2j
              </button>
              <button
                type="button"
                (click)="
                  selectedProduct.perishability = 'low'; onProductFieldChange()
                "
                class="flex-1 py-2 px-1 rounded-lg border-2 text-xs font-medium transition-all"
                [class.border-sky-400]="selectedProduct.perishability === 'low'"
                [class.bg-sky-50]="selectedProduct.perishability === 'low'"
                [class.text-sky-700]="selectedProduct.perishability === 'low'"
                [class.border-gray-200]="
                  selectedProduct.perishability !== 'low'
                "
                [class.text-gray-500]="selectedProduct.perishability !== 'low'"
              >
                🧊 Stock
              </button>
            </div>
          </div>
          <!-- Conservation -->
          <div>
            <label
              class="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1"
              >Conservation</label
            >
            <select
              [(ngModel)]="selectedProduct.shelfLife"
              (ngModelChange)="onProductFieldChange()"
              class="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm font-medium"
            >
              <option value="jour même">📅 Jour même</option>
              <option value="2 jours">📅 2 jours</option>
              <option value="3 jours">📅 3 jours</option>
              <option value="1 semaine">📅 1 semaine</option>
              <option value="1 mois">📅 1 mois</option>
              <option value="6 mois">📅 6 mois</option>
              <option value="1 an">📅 1 an</option>
            </select>
          </div>
          <!-- Actions -->
          <div
            class="pt-3 border-t border-gray-100 dark:border-slate-700 flex gap-2"
          >
            <button
              (click)="removeProduct(selectedProduct); selectedProduct = null"
              class="flex-1 px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Retirer
            </button>
            <button
              (click)="confirmProduct()"
              class="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              [disabled]="saving"
            >
              @if (!saving) {
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              }
              @if (saving) {
                <svg
                  class="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              }
              {{ saving ? "Sauvegarde..." : "Confirmer" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</div>
